/**
 * Связывает WS-клиент со store.
 *
 * Клиент живёт вне React и не знает про Redux; store не знает про сокет.
 * Связь односторонняя и собирается здесь, один раз при старте.
 */

import { BASE_PATH } from "@/shared/api/basePath";
import {
  api,
  EVENT_INVALIDATIONS,
  getAccessToken,
  initWsClient,
  sessionLost,
  type WsError,
} from "@taskflow/client-core";
import { reportsApi } from "@taskflow/client-core/reports";
import { resolveApiUrl } from "@/shared/api/http";
import type { EventName } from "@taskflow/contracts";

import { refreshAccessToken } from "@/shared/auth/httpAuth";
import { toast } from "@/shared/ui";

import { applyTaskMovedEvent } from "@taskflow/client-core/tasks";

import { connectionChanged, syncLost } from "./uiSlice";
import { store } from "./store";

/**
 * Адрес сокета.
 *
 * По умолчанию — тот же источник, откуда взята страница: в продакшне
 * фронтенд и API стоят за одним nginx, и настраивать там нечего.
 * Схема выводится из схемы страницы, иначе на https получился бы
 * незашифрованный ws://, который браузер попросту заблокирует.
 *
 * Относительный путь («/ws») разворачивается в абсолютный: конструктор
 * WebSocket относительных адресов не принимает.
 */
function resolveWsUrl(): string {
  const configured = import.meta.env.VITE_WS_URL;
  if (configured !== undefined && configured !== "" && !configured.startsWith("/")) {
    return configured;
  }
  // По умолчанию — под префиксом приложения: путь «/ws» в корне общего
  // Apache может принадлежать другому приложению.
  const path = configured === undefined || configured === "" ? `${BASE_PATH}/ws` : configured;
  const scheme = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${scheme}//${window.location.host}${path}`;
}

const WS_URL = resolveWsUrl();

export function bootstrap(): void {
  const client = initWsClient({
    url: WS_URL,
    getAccessToken,
    refreshAccessToken,
    onSessionLost: (reason) => {
      store.dispatch(sessionLost(reason));
    },
  });

  client.onStateChange((state) => {
    store.dispatch(connectionChanged(state));
  });

  client.onEvent((event) => {
    // Докатить пропущенное не удалось — кэш может расходиться с сервером.
    // Сбрасываем всё: показывать устаревшую доску молча недопустимо.
    if (event.event === "sync.lost") {
      store.dispatch(syncLost());
      const everything = Object.values(EVENT_INVALIDATIONS).flatMap((tags) => tags ?? []);
      store.dispatch(api.util.invalidateTags([...new Set(everything)]));
      return;
    }

    // Протокол §6: чужое перемещение — точечное обновление кэша, а не
    // перезапрос всей доски на каждое перетаскивание.
    if (event.event === "task.moved") {
      // Точечно в кэш — то же правило, что в приложении (client-core/tasks/events.ts).
      applyTaskMovedEvent(store.dispatch, store.getState(), event.payload);
      return;
    }

    // Сессию отозвали на сервере — продолжать работу этим токеном нельзя.
    if (event.event === "session.revoked") {
      store.dispatch(sessionLost("Сессия завершена: доступ отозван"));
      client.disconnect();
      return;
    }

    // §25.4: участника удалили с доски — доступ теряется немедленно.
    if (event.event === "membership.revoked") {
      toast.warning("Доступ к доске отозван", "Вас удалили из участников доски");
      store.dispatch(api.util.invalidateTags(["Board", "BoardList", "TaskList", "Archive"]));
      return;
    }

    // §21.8: выгрузка готова. Ссылка берётся по нажатию: подписанная
    // ссылка живёт минуты, а уведомление может провисеть дольше.
    if (event.event === "export.ready") {
      const payload = event.payload as { jobId?: unknown; format?: unknown };
      if (typeof payload.jobId === "string") {
        const jobId = payload.jobId;
        const download = async () => {
          try {
            const status = await store
              .dispatch(reportsApi.endpoints.exportStatus.initiate({ jobId }))
              .unwrap();
            const url = status.status === "READY" ? resolveApiUrl(status.downloadUrl) : null;
            if (url === null) {
              toast.warning("Файл ещё не готов", "Попробуйте через минуту");
              return;
            }
            window.location.assign(url);
          } catch (raw) {
            toast.error(raw as WsError, "Не удалось скачать выгрузку");
          }
        };
        toast.action(
          "Выгрузка готова",
          typeof payload.format === "string" ? `Файл ${payload.format} сформирован` : "Файл сформирован",
          "Скачать",
          () => void download(),
        );
      }
      return;
    }

    // Поле event — строка: клиент порождает и свои события (sync.lost).
    const tags = EVENT_INVALIDATIONS[event.event as EventName];
    if (tags) {
      store.dispatch(api.util.invalidateTags(tags));
    }
    // Событий без правила инвалидации быть не должно, но падать из-за этого
    // нельзя: сервер может добавить новое раньше клиента.
  });
}
